'use client';

import Image from 'next/image';
import type { ReactNode } from 'react';
import { CheckCircle2, Circle, Sparkles, Zap, Shirt } from 'lucide-react';

const SHOE_IMAGE = '/styleai/offwhite-dunks.jpg';

const generatedItems = [
  {
    category: 'Outerwear',
    name: 'Racing Jacket',
    price: '$165',
    image: '/styleai/racing-jacket.png',
    imageClassName: 'scale-[1.7]',
  },
  {
    category: 'Tops',
    name: "Black Women's Tank Top",
    price: '$45',
    image: '/styleai/crop-tank.png',
    imageClassName: 'scale-[1.66]',
  },
  {
    category: 'Bottoms',
    name: 'Washed Black Cargo Pants',
    price: '$110',
    image: '/styleai/black-pants.png',
    imageClassName: 'scale-[1.7]',
  },
];

export default function HowItWorksSection() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-black px-5 pb-16 pt-[92px] text-white lg:px-8">
      {/* Background */}
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.18]"
        style={{
          backgroundImage: "url('/styleai/lab-bg.jpg')",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-black/86" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_16%,rgba(245,200,66,0.07),transparent_28%),radial-gradient(circle_at_15%_8%,rgba(245,200,66,0.045),transparent_26%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/10" />
      <div className="pointer-events-none absolute left-[28%] top-[118px] h-[2px] w-[280px] bg-gradient-to-r from-transparent via-[#f5c842]/55 to-transparent blur-[1px]" />

      <div className="relative mx-auto max-w-[1500px]">
        <div className="mb-4">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.48em] text-[#f5c842]">
            How It Works
          </p>

          <h2 className="text-[40px] font-black uppercase leading-none tracking-[-0.045em] md:text-[58px] xl:text-[66px]">
            Build <span className="text-[#f5c842]">Your</span> Fit
          </h2>

          <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-white/68">
            Drop your vibe, set your budget, and FHITZ builds a real outfit around your pieces.
          </p>
        </div>

        <div className="grid items-stretch gap-5 lg:grid-cols-3">
          {/* 01 */}
          <div className="stylelab-slide-left flex h-[560px] rounded-[24px] border border-white/12 bg-[#050505]/88 p-5 shadow-[0_28px_90px_rgba(0,0,0,0.72)] backdrop-blur-md">
            <div className="flex min-h-0 w-full flex-col">
              <Header
                number="01"
                title="Your Vibe"
                description="Tell us your vibe, colors, brands, or the occasion."
              />

              <div className="mt-4 rounded-[18px] border border-white/10 bg-black/52 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <p className="text-[17px] font-semibold italic leading-7 text-white/92">
                  “I have these red Off-White dunks and I’m looking for a racing jacket style fit.”
                </p>
              </div>

              <div className="mt-4 overflow-hidden rounded-[18px] border border-white/10 bg-[#0b0b0b]/88 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <Image
                  src={SHOE_IMAGE}
                  alt="Red Off-White dunks"
                  width={1200}
                  height={800}
                  className="h-[250px] w-full scale-[1.12] object-contain p-3 xl:h-[270px]"
                  priority
                />
              </div>

              <div className="mt-4 flex h-[58px] items-center gap-4 rounded-[16px] border border-white/10 bg-black/58 px-5">
                <Sparkles className="h-5 w-5 shrink-0 text-[#f5c842]" />

                <p className="min-w-0 flex-1 truncate text-[15px] font-black">
                  FHITZ is analyzing your items...
                </p>

                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#f5c842]" />
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#f5c842]/70" />
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white/32" />
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white/22" />
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white/14" />
                </div>
              </div>
            </div>
          </div>

          {/* 02 */}
          <div className="stylelab-slide-up flex h-[560px] rounded-[24px] border border-white/12 bg-[#050505]/88 p-5 shadow-[0_28px_90px_rgba(0,0,0,0.72)] backdrop-blur-md">
            <div className="flex min-h-0 w-full flex-col">
              <Header number="02" title="Your Preferences" description="Fine-tune your fit." />

              <div className="mt-4 flex h-[50px] items-center justify-between rounded-xl border border-[#3a3a3a] bg-[#1b1b1b] px-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] font-black uppercase tracking-wide text-[#f5c842]">
                    Women&apos;s Streetwear
                  </span>
                </div>

                <span className="text-xl leading-none text-[#f5c842]">⌄</span>
              </div>

              <div className="mt-3 space-y-2.5">
                <Preference label="Outerwear" icon={<CoatIcon />} selected />
                <Preference label="Tops" icon={<Shirt className="h-5 w-5" />} selected />
                <Preference label="Bottoms" icon={<PantsIcon />} selected />
                <Preference label="Shoes" icon={<ShoeIcon />} />
                <Preference label="Accessories" icon={<HatIcon />} />
              </div>

              <div className="mt-4 px-2">
                <div className="mb-4 flex items-center justify-between px-1">
                  <p className="text-[15px] font-black uppercase tracking-wide">Budget</p>
                  <p className="text-[18px] font-black text-[#f5c842]">$350</p>
                </div>

                <div className="relative mx-auto h-2.5 w-full rounded-full bg-white/12">
                  <div className="h-2.5 w-[42%] rounded-full bg-[#f5c842]" />
                  <div className="absolute left-[42%] top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#f5c842] text-lg font-black text-black shadow-[0_0_24px_rgba(245,200,66,0.3)]">
                    $
                  </div>
                </div>
              </div>

              <div className="mt-5 flex h-[50px] items-center gap-3 rounded-[16px] border border-[#2a2a2a] bg-black/60 px-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <Zap className="h-5 w-5 shrink-0 text-[#f5c842]" />
                <p className="truncate text-[13px] font-black leading-none">
                  Perfect pieces within your budget.
                </p>
              </div>
            </div>
          </div>

          {/* 03 */}
          <div className="stylelab-slide-right flex h-[560px] rounded-[24px] border border-white/12 bg-[#050505]/88 p-5 shadow-[0_28px_90px_rgba(0,0,0,0.72)] backdrop-blur-md">
            <div className="flex min-h-0 w-full flex-col">
              <Header number="03" title="Generated Outfit" description="Your custom outfit is ready." />

              <div className="mt-4 space-y-2.5">
                {generatedItems.map((item) => (
                  <div
                    key={item.category}
                    className="grid h-[132px] grid-cols-[46%_1fr] overflow-hidden rounded-[16px] border border-white/10 bg-black/58 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                  >
                    <div className="flex h-full items-center justify-center overflow-hidden border-r border-white/10 bg-[#111111]">
                      <Image
                        src={item.image}
                        alt={item.name}
                        width={420}
                        height={320}
                        className={`h-full w-full object-contain p-0 ${item.imageClassName}`}
                      />
                    </div>

                    <div className="flex min-w-0 flex-col justify-center px-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45">
                        {item.category}
                      </p>
                      <p className="mt-1 text-[16px] font-semibold leading-tight text-white">
                        {item.name}
                      </p>
                      <p className="mt-2 text-[16px] font-black text-[#f5c842]">{item.price}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-[#f5c842]/35 pt-3">
                <p className="text-[15px] font-black uppercase tracking-wide">Total</p>
                <p className="text-[22px] font-black text-[#f5c842]">$320</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-4 hidden items-center gap-5 text-center text-xs font-medium text-white/34 lg:flex">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#f5c842]/45 to-transparent" />
          <p className="shrink-0">Real items. Real prices. Real checkout links.</p>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#f5c842]/45 to-transparent" />
        </div>
      </div>

      <style jsx global>{`
        @keyframes stylelabSlideLeft {
          from {
            opacity: 0;
            transform: translateX(-42px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes stylelabSlideUp {
          from {
            opacity: 0;
            transform: translateY(34px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes stylelabSlideRight {
          from {
            opacity: 0;
            transform: translateX(42px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .stylelab-slide-left {
          animation: stylelabSlideLeft 0.8s ease-out both;
        }

        .stylelab-slide-up {
          animation: stylelabSlideUp 0.8s ease-out 0.12s both;
        }

        .stylelab-slide-right {
          animation: stylelabSlideRight 0.8s ease-out 0.24s both;
        }
      `}</style>
    </section>
  );
}

function Header({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center text-[24px] font-black leading-none text-[#f5c842]">
        {number}
      </div>

      <div>
        <h3 className="text-[17px] font-black uppercase tracking-wide">{title}</h3>
        <p className="mt-1 text-[13px] text-white/62">{description}</p>
      </div>
    </div>
  );
}

function Preference({
  label,
  icon,
  selected = false,
}: {
  label: string;
  icon: ReactNode;
  selected?: boolean;
}) {
  return (
    <div
      className={`flex h-[50px] items-center justify-between rounded-xl border border-[#3a3a3a] px-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${
        selected
          ? 'bg-[#1b1b1b] text-white'
          : 'bg-[#050505] text-white/58'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={selected ? 'text-[#f5c842]' : 'text-white/35'}>{icon}</div>
        <span className="text-[14px] font-medium">{label}</span>
      </div>

      {selected ? (
        <CheckCircle2 className="h-5 w-5 fill-[#f5c842] text-black" />
      ) : (
        <Circle className="h-5 w-5 text-white/40" />
      )}
    </div>
  );
}

function CoatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M8 4L5 7v13h5v-7h4v7h5V7l-3-3-2 2h-4L8 4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PantsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M8 4h8l2 16h-5l-1-8-1 8H6L8 4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShoeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M4 15c4 1 6-1 8-4l4 4h4c1 0 2 1 2 2v2H3v-4h1Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M5 13h14l1 5H4l1-5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M8 13c.5-4 2-7 4-7s3.5 3 4 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}